import { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import { ArrowLeft, Calendar, FileDown, FileSpreadsheet } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useToast } from "~/hooks/use-toast";
import { useCompanies, useMappingTemplates } from "~/hooks/contabilidad";
import { useBalanceSheet } from "~/hooks/contabilidad/useBalanceSheet";
import { balanceSheetService } from "~/services/contabilidad/balanceSheetService";
import { exportBalanceSheetToExcel } from "~/services/contabilidad/balanceSheetExportService";

import { BalanceSheetTable } from "./components/BalanceSheetTable";

const ALIACE_SPA_ID = "809d3197-9371-4cbb-b544-aadbd29940d6";

// Generate month options for the last 24 months
function generateMonthOptions() {
  const options = [];
  const now = dayjs();
  for (let i = 0; i < 24; i++) {
    const date = now.subtract(i, "month");
    options.push({
      value: date.format("YYYY-MM"),
      label: date.format("MMMM YYYY"),
    });
  }
  return options;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export default function BalanceSheetPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const monthOptions = useMemo(() => generateMonthOptions(), []);

  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("YYYY-MM"));
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [templateFilter, setTemplateFilter] = useState<string>("");
  const [isExporting, setIsExporting] = useState(false);

  // Calculate date range from selected month
  const dateFrom = dayjs(selectedMonth).startOf("month").format("YYYY-MM-DD");
  const dateTo = dayjs(selectedMonth).endOf("month").format("YYYY-MM-DD");

  const { data: companies = [] } = useCompanies();
  const { data: templates = [] } = useMappingTemplates({
    company_id: companyFilter,
    is_active: true,
  });

  const { data: lines = [], isLoading } = useBalanceSheet({
    dateFrom,
    dateTo,
    companyId: companyFilter,
    templateId: templateFilter,
  });

  // Group the data for display
  const groupedData = useMemo(() => {
    if (lines.length === 0) return null;
    return balanceSheetService.groupBalanceSheetData(lines);
  }, [lines]);

  // Reset template filter when company changes
  const handleCompanyChange = (value: string) => {
    setCompanyFilter(value);
    setTemplateFilter("");
  };

  // Handle export
  const handleExport = () => {
    if (!groupedData) return;

    setIsExporting(true);
    try {
      const selectedCompany = companies.find((c) => c.id === companyFilter);
      const periodLabel = dayjs(selectedMonth).format("MMMM YYYY");

      exportBalanceSheetToExcel({
        data: groupedData,
        period: periodLabel,
        companyName: selectedCompany?.name || "",
        templateName: selectedTemplate?.name || "",
      });

      toast({
        title: "Reporte exportado",
        description: "El archivo Excel se ha descargado correctamente.",
      });
    } catch (error) {
      console.error("Error exporting Balance Sheet:", error);
      toast({
        title: "Error",
        description: "No se pudo exportar el reporte.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Set default company to Aliace SPA
  useEffect(() => {
    if (companies.length > 0 && !companyFilter) {
      const aliaceSpa = companies.find((c) => c.id === ALIACE_SPA_ID);
      if (aliaceSpa) {
        setCompanyFilter(ALIACE_SPA_ID);
      } else if (companies.length === 1) {
        setCompanyFilter(companies[0].id);
      }
    }
  }, [companies, companyFilter]);

  // Auto-select first active template when company changes and templates load
  useEffect(() => {
    if (templates.length > 0 && !templateFilter && companyFilter) {
      setTemplateFilter(templates[0].id);
    }
  }, [templates, templateFilter, companyFilter]);

  const selectedTemplate = templates.find((t) => t.id === templateFilter);

  return (
    <div className="container mx-auto w-[80vw] space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/contabilidad/reportes")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Estado de Situacion Financiera</h1>
            <p className="text-sm text-muted-foreground">
              Balance general con activos, pasivos y patrimonio
            </p>
          </div>
        </div>
        <Button
          onClick={handleExport}
          disabled={!groupedData || isLoading || isExporting}
        >
          <FileDown className="mr-2 h-4 w-4" />
          Exportar Excel
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">Periodo Contable</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[200px] rounded-xl">
                  <Calendar className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Seleccionar periodo" />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Empresa <span className="text-red-500">*</span>
              </Label>
              <Select value={companyFilter} onValueChange={handleCompanyChange}>
                <SelectTrigger className="w-[200px] rounded-xl">
                  <SelectValue placeholder="Seleccionar empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Plantilla de Mapeo <span className="text-red-500">*</span>
              </Label>
              <Select
                value={templateFilter}
                onValueChange={setTemplateFilter}
                disabled={!companyFilter}
              >
                <SelectTrigger className="w-[250px] rounded-xl">
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Seleccionar plantilla" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                      {template.mappings_count !== undefined && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({template.mappings_count} cuentas)
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {!companyFilter || !templateFilter ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-muted-foreground">
              Selecciona una empresa y una plantilla de mapeo para ver el Estado
              de Situacion Financiera.
            </p>
            <p className="text-sm text-muted-foreground">
              Ambos campos son requeridos para generar el reporte.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary cards */}
          {groupedData && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total Activo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(groupedData.totalActivo)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total Pasivo y Patrimonio
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(groupedData.totalPasivoYPatrimonio)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Diferencia
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p
                    className={`text-2xl font-bold ${
                      Math.abs(groupedData.diferencia) < 1
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {formatCurrency(groupedData.diferencia)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Cuentas en Reporte
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{lines.length}</p>
                </CardContent>
              </Card>
            </div>
          )}

          <Card>
            <CardHeader>
              <CardTitle>
                Estado de Situacion Financiera
                {selectedTemplate && (
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    (Plantilla: {selectedTemplate.name})
                  </span>
                )}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {dayjs(selectedMonth).format("MMMM YYYY")} - Al cierre del
                periodo
              </p>
            </CardHeader>
            <CardContent>
              <BalanceSheetTable data={groupedData} isLoading={isLoading} />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
